const POLYMARKET_BASE_URL = 'https://gamma-api.polymarket.com'

// Raw API response types
export type PolymarketTag = {
	id: string
	label: string
	slug: string
}

export type PolymarketFeeSchedule = {
	rate: number // e.g., 0.072 (7.2%)
	exponent: number // typically 1
	takerOnly: boolean
	rebateRate?: number // maker rebate rate
}

export type PolymarketMarket = {
	id: string
	question: string
	conditionId: string
	slug: string
	resolutionSource: string
	endDate: string
	liquidity: string
	startDate: string
	eventStartTime?: string
	image: string
	icon: string
	description: string
	outcomes: string // JSON string like '["Yes", "No"]'
	outcomePrices: string | number[] // JSON string like '["0.55", "0.45"]' or array from carousel API
	volume: string
	events?: PolymarketEvent[]
	// Polymarket market status flags:
	// - active: Market is open for trading
	// - closed: Market is closed/resolved
	// - archived: Market is hidden from main feeds (old or removed)
	// - new: Market is recently created/featured
	active: boolean
	closed: boolean
	archived?: boolean
	new?: boolean
	marketMakerAddress: string
	volumeNum: number
	liquidityNum?: number
	clobTokenIds: string | string[] // JSON string or array from carousel API
	bestBid?: number
	bestAsk?: number
	lastTradePrice?: number
	oneDayPriceChange?: number
	oneWeekPriceChange?: number
	groupItemTitle?: string // e.g. "Atlanta" for Super Bowl teams
	sportsMarketType?: string // e.g. 'moneyline', 'spread', 'total', 'both_teams_to_score' changes based on sport type
	line?: number // spread/total line value
	negRisk?: boolean // per-market negRisk (overrides event-level when present)
	acceptingOrders?: boolean // Whether the CLOB is accepting new orders for this market
	closedTime?: string // ISO timestamp when the market was closed/resolved
	// Fee-related fields from Gamma API
	feesEnabled?: boolean
	feeType?: string // e.g., 'crypto_fees_v2'
	feeSchedule?: PolymarketFeeSchedule
}

export type PolymarketEvent = {
	id: string
	ticker: string
	slug: string
	title: string
	description: string
	resolutionSource: string
	startDate: string
	endDate: string
	image: string
	icon: string
	active: boolean
	closed: boolean
	liquidity: number
	volume: number
	volume24hr?: number
	commentCount: number
	markets: PolymarketMarket[]
	tags: PolymarketTag[]
	negRisk?: boolean
	competitive?: number
	gameId?: string
	score?: string
	live?: boolean
	teams?: string
	startTime?: string // ISO 8601 event start time (crypto binary events)
	seriesSlug?: string
	series?: Array<{
		id: string
		ticker: string
		slug: string
		title: string
		recurrence: string
		active: boolean
		closed: boolean
	}>
}

export type PolymarketEventsResponse = PolymarketEvent[]

export type PolymarketSearchResponse = {
	events?: PolymarketEvent[] | null
	pagination?: {
		hasMore?: boolean
		totalResults?: number
	}
}

// Raw team data from /teams endpoint
export type PolymarketTeam = {
	id: number
	name: string
	league: string
	abbreviation: string
	logo?: string
	color?: string
	record?: string
	alias?: string
}

// Fetch teams by league and optional name filter
export async function fetchPolymarketTeams(
	league: string,
	names?: string[]
): Promise<PolymarketTeam[]> {
	const params = new URLSearchParams({ league })

	if (names && names.length > 0) {
		for (const name of names) {
			params.append('abbreviation', name)
		}
	}

	const url = `${POLYMARKET_BASE_URL}/teams?${params.toString()}`

	const res = await fetch(url, { next: { revalidate: 300 } })

	if (!res.ok) return []

	return (await res.json()) as PolymarketTeam[]
}

// Fetch events list
export async function fetchPolymarketEvents(params?: {
	limit?: number
	offset?: number
	order?: 'volume' | 'liquidity' | 'createdAt' | 'id'
	ascending?: boolean
	// Status booleans - all can be set independently
	active?: boolean
	closed?: boolean
	archived?: boolean
	tag?: string
}): Promise<PolymarketEventsResponse> {
	const searchParams = new URLSearchParams()

	searchParams.set('limit', (params?.limit ?? 50).toString())
	if (params?.offset) {
		searchParams.set('offset', params.offset.toString())
	}
	searchParams.set('order', params?.order ?? 'volume')
	searchParams.set('ascending', (params?.ascending ?? false).toString())
	// Status boolean filters
	// if (params?.active !== undefined) {
	// 	searchParams.set('active', params.active.toString())
	// }
	if (params?.closed !== undefined) {
		searchParams.set('closed', params.closed.toString())
	}
	// if (params?.archived !== undefined) {
	// 	searchParams.set('archived', params.archived.toString())
	// }
	if (params?.tag) {
		searchParams.set('tag_slug', params.tag)
	}

	const url = `${POLYMARKET_BASE_URL}/events?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const body = await res.json()
	// Polymarket has two response shapes depending on endpoint:
	// - GET /events returns an array of events (body is Array)
	// - GET /public-search returns an object with `events` property
	// Support both shapes and return a stable array to callers.
	if (Array.isArray(body)) {
		return body as PolymarketEventsResponse
	}
	return body?.events ?? []
}

// Fetch single event by slug
export async function fetchPolymarketEvent(slug: string): Promise<PolymarketEvent> {
	const url = `${POLYMARKET_BASE_URL}/events?slug=${encodeURIComponent(slug)}`
	const res = await fetch(url, { cache: 'no-store' })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const events = await res.json()
	if (!events || events.length === 0) {
		throw new Error(`Event not found: ${slug}`)
	}

	return events[0]
}

// Fetch single event by slug path (used for sports companion events like {slug}-more-markets)
export async function fetchPolymarketEventBySlug(slug: string): Promise<PolymarketEvent> {
	const url = `${POLYMARKET_BASE_URL}/events/slug/${encodeURIComponent(slug)}`
	const res = await fetch(url, { next: { revalidate: 10 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Fetch single event by ID
export async function fetchPolymarketEventById(id: string): Promise<PolymarketEvent> {
	const url = `${POLYMARKET_BASE_URL}/events?id=${encodeURIComponent(id)}`
	const res = await fetch(url, { next: { revalidate: 10 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const events = await res.json()
	if (!events || events.length === 0) {
		throw new Error(`Event not found: ${id}`)
	}

	return events[0]
}

// Fetch markets directly (flat list, not grouped by event)
// Note: Use 'volumeNum'/'liquidityNum' for proper numeric sorting (string fields sort alphabetically)
export async function fetchPolymarketMarkets(params?: {
	limit?: number
	offset?: number
	order?: string // volumeNum, liquidityNum, volume24hr, createdAt, startDate, etc.
	ascending?: boolean
	// Status booleans - all can be set independently
	active?: boolean
	closed?: boolean
	archived?: boolean
}): Promise<PolymarketMarket[]> {
	const searchParams = new URLSearchParams()

	searchParams.set('limit', (params?.limit ?? 50).toString())
	if (params?.offset) {
		searchParams.set('offset', params.offset.toString())
	}
	searchParams.set('order', params?.order ?? 'volumeNum')
	searchParams.set('ascending', (params?.ascending ?? false).toString())
	// Status boolean filters
	// if (params?.active !== undefined) {
	// 	searchParams.set('active', params.active.toString())
	// }
	if (params?.closed !== undefined) {
		searchParams.set('closed', params.closed.toString())
	}
	// if (params?.archived !== undefined) {
	// 	searchParams.set('archived', params.archived.toString())
	// }

	const url = `${POLYMARKET_BASE_URL}/markets?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Helper to parse outcome prices
export function parseOutcomePrices(outcomePrices: string | number[]): [number, number] {
	try {
		const parsed = Array.isArray(outcomePrices) ? outcomePrices : JSON.parse(outcomePrices)
		return [Number.parseFloat(parsed[0]) || 0, Number.parseFloat(parsed[1]) || 0]
	} catch {
		return [0.5, 0.5]
	}
}

// Helper to parse CLOB token IDs
export function parseClobTokenIds(clobTokenIds: string | string[]): [string, string] {
	try {
		const parsed = Array.isArray(clobTokenIds) ? clobTokenIds : JSON.parse(clobTokenIds)
		return [parsed[0] || '', parsed[1] || '']
	} catch {
		return ['', '']
	}
}

// Fetch all sports markets for an event (main + companion "-more-markets" event)
// Silently handles missing companion events — never throws for missing companions.
export type PolymarketSportsMarketsResult = {
	event: {
		id: string
		slug: string
		title: string
		startDate: string
		endDate: string
		active: boolean
		closed: boolean
		volume: number
		liquidity: number
		score?: string
		period?: string
		elapsed?: string
		gameId?: string
		teams?: string
		live?: boolean
		ended?: boolean
		startTime?: string
		image?: string
		tags?: PolymarketTag[]
	}
	marketsByType: Record<string, PolymarketMarket[]>
	marketTypes: string[]
	totalMarkets: number
}

export async function fetchPolymarketSportsMarkets(
	slug: string
): Promise<PolymarketSportsMarketsResult> {
	const mainEvent = await fetchPolymarketEventBySlug(slug)

	const allMarkets: PolymarketMarket[] = [...(mainEvent.markets || [])]

	// Try fetching the "-more-markets" companion event (sports pattern)x
	try {
		const moreEvent = await fetchPolymarketEventBySlug(`${slug}-more-markets`)

		allMarkets.push(...(moreEvent.markets || []))
	} catch {
		// No companion event — all markets are in the main event
	}

	// Group by sportsMarketType
	const marketsByType: Record<string, PolymarketMarket[]> = {}
	for (const m of allMarkets) {
		const type = m.sportsMarketType || 'unknown'
		if (!marketsByType[type]) marketsByType[type] = []
		marketsByType[type].push(m)
	}

	return {
		event: {
			id: mainEvent.id,
			slug: mainEvent.slug,
			title: mainEvent.title,
			startDate: mainEvent.startDate,
			endDate: mainEvent.endDate,
			score: mainEvent.score,
			active: mainEvent.active,
			closed: mainEvent.closed,
			volume: mainEvent.volume,
			live: mainEvent.live,
			liquidity: mainEvent.liquidity,
			gameId: mainEvent.gameId,
			teams: mainEvent.teams,
			startTime: (mainEvent as Record<string, unknown>).startTime as string | undefined,
			image: mainEvent.image,
			tags: mainEvent.tags,
		},
		marketsByType,
		marketTypes: Object.keys(marketsByType),
		totalMarkets: allMarkets.length,
	}
}

// Search public endpoint (returns markets/events/profiles). We request only events here.
export async function fetchPolymarketSearch(
	query: string,
	params?: { limit?: number; offset?: number }
): Promise<PolymarketSearchResponse> {
	const searchParams = new URLSearchParams()

	// Polymarket expects `q` as the query parameter
	searchParams.set('q', query)

	const limit = params?.limit ?? 50
	// Use `limit_per_type` and page (1-indexed) per Polymarket API
	searchParams.set('limit_per_type', limit.toString())

	const offset = params?.offset ?? 0
	const page = Math.floor(offset / limit) + 1
	searchParams.set('page', page.toString())

	// Restrict results to events only
	searchParams.set('types', 'events')

	const url = `${POLYMARKET_BASE_URL}/public-search?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const body: PolymarketSearchResponse = await res.json()
	return body
}
